import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { EDI_MAPPING_MODELLER_FEATURE_ID } from '../../../models/edi-mapping-modeller';
import { EdiDocumentView, EdiSourceNode, EdiViewBlock } from '../../../models/edi-viewer';
import { AuthService } from '../../../services/auth/auth.service';
import { BackendFeatureService } from '../../../services/backend-feature.service';
import { EdiService } from '../../../services/edi/edi.service';
import { EdiViewerCompatibilityService } from '../../../services/edi/edi-viewer-compatibility.service';
import { EdiViewRegenerationRequest } from '../edi-view-regeneration-dialog/edi-view-regeneration-dialog.component';

type ViewerTab = 'friendly' | 'structure' | 'raw' | 'mapping';

interface RawSourcePart {
  text: string;
  highlighted: boolean;
}

interface RawSourceLine {
  text: string;
  segmentIndex?: number;
  row?: number;
  line?: number;
  parts: RawSourcePart[];
}

interface RawSourceRange {
  line: number;
  start: number;
  end: number;
}

interface XmlPathFrame {
  path: string;
  childCounts: Record<string, number>;
  textCount: number;
}

@Component({
  selector: 'lib-edi-document-viewer',
  templateUrl: './edi-document-viewer.component.html',
  styleUrls: ['./edi-document-viewer.component.scss'],
  standalone: false
})
export class EdiDocumentViewerComponent implements OnChanges, OnInit {
  @Input() view?: EdiDocumentView;
  @Input() messageId?: string;
  @Input() messageType?: string;
  @Input() useAi = true;

  activeTab: ViewerTab = 'friendly';
  loading = false;
  error?: string;
  nodeIndex: Record<string, EdiSourceNode> = {};
  nodeParentIndex: Record<string, string | undefined> = {};
  fallbackNodes: EdiSourceNode[] = [];
  selectedNodes: EdiSourceNode[] = [];
  sourceProvenanceExpanded = true;
  selectedSourceNodeIds: ReadonlySet<string> = new Set<string>();
  expandedSourceNodeIds: ReadonlySet<string> = new Set<string>();
  scrollTargetNodeId?: string;
  technicalOpen = false;
  technicalLimit = 200;
  rawDisplay = '';
  rawSourceLines: RawSourceLine[] = [];
  xmlSourceRanges: Record<string, RawSourceRange[]> = {};
  compatibilityWarning?: string;
  canViewRawSource = false;
  mappingModellerEnabled = false;
  mappingFile?: File;
  regenerationDialogOpen = false;

  constructor(
    private ediService: EdiService,
    private compatibilityService: EdiViewerCompatibilityService,
    private authService: AuthService,
    private backendFeatureService: BackendFeatureService
  ) {}

  ngOnInit(): void {
    this.compatibilityService.getCompatibility().subscribe(result => (this.compatibilityWarning = result.message));
    this.authService.getHasRoles('ACTUATOR').subscribe(canViewRawSource => {
      this.canViewRawSource = canViewRawSource;
      if (!canViewRawSource && this.activeTab === 'raw') this.activeTab = 'friendly';
      if (!canViewRawSource) this.closeMapping();
    });
    this.backendFeatureService
      .isActivated(EDI_MAPPING_MODELLER_FEATURE_ID)
      .subscribe(enabled => {
        this.mappingModellerEnabled = enabled;
        if (!enabled) this.closeMapping();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.view && this.view) this.prepareView();
    if ((changes.messageId || changes.messageType || changes.useAi) && this.messageId && this.messageType) {
      this.loadMessage();
    }
  }

  get friendlyBlocks(): EdiViewBlock[] {
    return (this.view?.definition?.blocks || []).filter(block => block.type !== 'SOURCE_EXPLORER');
  }

  get visibleFallbackNodes(): EdiSourceNode[] {
    return this.fallbackNodes.slice(0, this.technicalLimit);
  }

  get viewerWarnings(): string[] {
    return Array.from(
      new Set(
        [this.compatibilityWarning, ...(this.view?.warnings || [])].filter((warning): warning is string => !!warning)
      )
    );
  }

  get canCreateMapping(): boolean {
    return this.canViewRawSource && this.mappingModellerEnabled;
  }

  get canRegenerateView(): boolean {
    return !!this.messageId && !!this.messageType && this.useAi;
  }

  regenerateView(): void {
    if (!this.canRegenerateView || this.loading) return;
    this.regenerationDialogOpen = true;
  }

  confirmRegeneration(request: EdiViewRegenerationRequest): void {
    this.regenerationDialogOpen = false;
    this.loadMessage(true, request);
  }

  openMapping(): void {
    if (!this.canCreateMapping || !this.view?.source?.rawContent) return;
    if (!this.mappingFile) {
      this.mappingFile = new File([this.view.source.rawContent], this.view.source.fileName || 'edi-document.txt', {
        type: this.view.source.mediaType || 'text/plain'
      });
    }
    this.clearSelection();
    this.activeTab = 'mapping';
  }

  closeMapping(): void {
    if (this.activeTab === 'mapping') this.activeTab = 'friendly';
    this.mappingFile = undefined;
  }

  selectSource(ids: string[]): void {
    this.selectedNodes = ids.map(id => this.nodeIndex[id]).filter(node => !!node);
    this.selectedSourceNodeIds = new Set(this.selectedNodes.map(node => node.id));
    this.expandedSourceNodeIds = this.selectedNodes.reduce((expanded, node) => {
      let parentId = this.nodeParentIndex[node.id];
      while (parentId) {
        expanded.add(parentId);
        parentId = this.nodeParentIndex[parentId];
      }
      return expanded;
    }, new Set<string>());
    this.scrollTargetNodeId = this.selectedNodes[0]?.id;
    this.updateRawSelection();
  }

  toggleSourceProvenance(): void {
    this.sourceProvenanceExpanded = !this.sourceProvenanceExpanded;
  }

  clearSelection(): void {
    this.selectedNodes = [];
    this.sourceProvenanceExpanded = true;
    this.selectedSourceNodeIds = new Set<string>();
    this.expandedSourceNodeIds = new Set<string>();
    this.scrollTargetNodeId = undefined;
    this.updateRawSelection();
  }

  selectTab(tab: ViewerTab): void {
    if (tab === 'raw' && !this.canViewRawSource) return;
    if (tab === 'mapping') {
      this.openMapping();
      return;
    }
    this.activeTab = tab;
  }

  showMoreTechnical(): void {
    this.technicalLimit += 500;
  }

  private loadMessage(forceRegenerate = false, regeneration?: EdiViewRegenerationRequest): void {
    this.loading = true;
    this.error = undefined;
    this.ediService
      .getDocumentView(
        this.messageId,
        this.messageType,
        this.useAi,
        forceRegenerate,
        regeneration?.instruction,
        regeneration?.persistInstruction
      )
      .subscribe({
        next: view => {
          this.view = view;
          this.loading = false;
          this.prepareView();
        },
        error: error => {
          this.loading = false;
          this.error = error?.error?.detail || error?.error?.message || 'The EDI source document could not be loaded.';
        }
      });
  }

  private prepareView(): void {
    this.closeMapping();
    this.nodeIndex = {};
    this.nodeParentIndex = {};
    for (const root of this.view?.source?.roots || []) this.indexNode(root);
    this.fallbackNodes = (this.view?.coverage?.fallbackSourceNodeIds || [])
      .map(id => this.nodeIndex[id])
      .filter(node => !!node);
    this.selectedNodes = [];
    this.selectedSourceNodeIds = new Set<string>();
    this.expandedSourceNodeIds = new Set<string>();
    this.scrollTargetNodeId = undefined;
    this.technicalLimit = 200;
    const rawContent = this.view?.source?.rawContent || '';
    this.rawSourceLines = this.buildRawSourceLines(rawContent, this.view?.source?.syntax);
    this.xmlSourceRanges = this.view?.source?.syntax === 'XML' ? this.buildXmlSourceRanges(rawContent) : {};
    this.rawDisplay = this.rawSourceLines.map(line => line.text).join('\n');
  }

  private indexNode(node: EdiSourceNode, parentId?: string): void {
    this.nodeIndex[node.id] = node;
    this.nodeParentIndex[node.id] = parentId;
    for (const child of node.children || []) this.indexNode(child, node.id);
  }

  private buildRawSourceLines(raw: string, syntax?: string): RawSourceLine[] {
    if (syntax === 'EDIFACT' || syntax === 'X12') {
      const terminator = this.segmentTerminator(raw, syntax);
      const release = syntax === 'EDIFACT' ? this.edifactReleaseCharacter(raw) : undefined;
      return this.splitSegments(raw, terminator, release).map((text, index) => ({
        text,
        segmentIndex: index + 1,
        parts: [{ text, highlighted: false }]
      }));
    }

    let sourceRow = 0;
    return raw.split(/\r?\n/).map((text, index) => {
      if (text.length > 0) sourceRow++;
      return {
        text,
        row: text.length > 0 ? sourceRow : undefined,
        line: index + 1,
        parts: [{ text, highlighted: false }]
      };
    });
  }

  private splitSegments(raw: string, terminator: string, release?: string): string[] {
    const segments: string[] = [];
    let current = '';
    let released = false;
    for (const character of raw) {
      current += character;
      if (release && character === release && !released) {
        released = true;
        continue;
      }
      if (character === terminator && !released) {
        const segment = current.trim();
        if (segment) segments.push(segment);
        current = '';
      }
      released = false;
    }
    const remainder = current.trim();
    if (remainder) segments.push(remainder);
    return segments;
  }

  private segmentTerminator(raw: string, syntax: string): string {
    const trimmed = raw.trimStart();
    if (syntax === 'EDIFACT' && trimmed.startsWith('UNA') && trimmed.length >= 9) return trimmed.charAt(8);
    if (syntax === 'X12' && trimmed.startsWith('ISA') && trimmed.length > 105) return trimmed.charAt(105);
    return syntax === 'EDIFACT' ? "'" : '~';
  }

  private edifactReleaseCharacter(raw: string): string {
    const trimmed = raw.trimStart();
    return trimmed.startsWith('UNA') && trimmed.length >= 7 ? trimmed.charAt(6) : '?';
  }

  private updateRawSelection(): void {
    const hasLocatedSelection = this.selectedNodes.some(node => this.hasSourceLocation(node));

    this.rawSourceLines = this.rawSourceLines.map(sourceLine => {
      const xmlRanges = this.selectedNodes.flatMap(node => this.xmlRangesOnLine(node, sourceLine.line));
      if (xmlRanges.length) {
        return {
          ...sourceLine,
          parts: this.highlightRanges(sourceLine.text, xmlRanges)
        };
      }
      const nodesAtLocation = this.selectedNodes.filter(node => this.matchesSourceLine(node, sourceLine));
      const nodesToHighlight = nodesAtLocation.length
        ? nodesAtLocation
        : hasLocatedSelection
          ? []
          : this.selectedNodes;
      const snippets = Array.from(
        new Set(nodesToHighlight.map(node => node.raw ?? node.value ?? '').filter(value => value.length > 0))
      );
      return {
        ...sourceLine,
        parts: this.highlightSnippets(sourceLine.text, snippets)
      };
    });
  }

  private hasSourceLocation(node: EdiSourceNode): boolean {
    const location = node.location || {};
    const xpath = location['xpath'];
    return (
      location['segmentIndex'] !== undefined ||
      location['row'] !== undefined ||
      location['line'] !== undefined ||
      (typeof xpath === 'string' && !!this.xmlSourceRanges[xpath]?.length)
    );
  }

  private matchesSourceLine(node: EdiSourceNode, sourceLine: RawSourceLine): boolean {
    const location = node.location || {};
    return (
      this.locationEquals(location['segmentIndex'], sourceLine.segmentIndex) ||
      this.locationEquals(location['row'], sourceLine.row) ||
      this.locationEquals(location['line'], sourceLine.line)
    );
  }

  private locationEquals(locationValue: unknown, sourceValue?: number): boolean {
    return sourceValue !== undefined && locationValue !== undefined && Number(locationValue) === sourceValue;
  }

  private highlightSnippets(text: string, snippets: string[]): RawSourcePart[] {
    const ranges: Array<{ start: number; end: number }> = [];
    for (const snippet of snippets) {
      let start = text.indexOf(snippet);
      while (start >= 0) {
        ranges.push({ start, end: start + snippet.length });
        start = text.indexOf(snippet, start + Math.max(1, snippet.length));
      }
    }
    return this.highlightRanges(text, ranges);
  }

  private highlightRanges(text: string, ranges: Array<{ start: number; end: number }>): RawSourcePart[] {
    if (!ranges.length) return [{ text, highlighted: false }];

    const merged = ranges
      .sort((left, right) => left.start - right.start || left.end - right.end)
      .reduce<Array<{ start: number; end: number }>>((result, range) => {
        const previous = result[result.length - 1];
        if (previous && range.start <= previous.end) previous.end = Math.max(previous.end, range.end);
        else result.push({ ...range });
        return result;
      }, []);

    const parts: RawSourcePart[] = [];
    let cursor = 0;
    for (const range of merged) {
      if (range.start > cursor) parts.push({ text: text.slice(cursor, range.start), highlighted: false });
      parts.push({ text: text.slice(range.start, range.end), highlighted: true });
      cursor = range.end;
    }
    if (cursor < text.length) parts.push({ text: text.slice(cursor), highlighted: false });
    return parts;
  }

  private xmlRangesOnLine(node: EdiSourceNode, line?: number): Array<{ start: number; end: number }> {
    const xpath = node.location?.['xpath'];
    if (typeof xpath !== 'string' || line === undefined) return [];
    return (this.xmlSourceRanges[xpath] || [])
      .filter(range => range.line === line)
      .map(range => ({ start: range.start, end: range.end }));
  }

  private buildXmlSourceRanges(raw: string): Record<string, RawSourceRange[]> {
    const result: Record<string, RawSourceRange[]> = {};
    const lines = raw.split(/\r?\n/);
    const lineStarts: number[] = [];
    let offset = 0;
    for (let index = 0; index < lines.length; index++) {
      lineStarts.push(offset);
      offset += lines[index].length;
      if (index < lines.length - 1) offset += raw.startsWith('\r\n', offset) ? 2 : 1;
    }

    const stack: XmlPathFrame[] = [];
    const rootCounts: Record<string, number> = {};
    const tokenPattern = /<!--[\s\S]*?-->|<!\[CDATA\[[\s\S]*?\]\]>|<\?[\s\S]*?\?>|<\/?[^>]+>/g;
    let previousEnd = 0;
    let token: RegExpExecArray | null;
    while ((token = tokenPattern.exec(raw))) {
      this.addXmlTextRange(raw, previousEnd, token.index, stack, result, lineStarts, lines);
      const markup = token[0];
      if (markup.startsWith('<![CDATA[')) {
        const frame = stack[stack.length - 1];
        if (frame) {
          frame.textCount++;
          this.addAbsoluteRange(
            result,
            `${frame.path}/#text[${frame.textCount}]`,
            token.index + 9,
            token.index + markup.length - 3,
            lineStarts,
            lines
          );
        }
      } else if (markup.startsWith('</')) {
        stack.pop();
      } else if (!markup.startsWith('<?') && !markup.startsWith('<!--') && !markup.startsWith('<!')) {
        const nameMatch = markup.match(/^<\s*([^\s/>]+)/);
        if (nameMatch) {
          const name = this.xmlLocalName(nameMatch[1]);
          const parent = stack[stack.length - 1];
          const siblingCounts = parent?.childCounts || rootCounts;
          const occurrence = (siblingCounts[name] || 0) + 1;
          siblingCounts[name] = occurrence;
          const path = parent ? `${parent.path}/${name}[${occurrence}]` : `/${name}[${occurrence}]`;
          this.addAbsoluteRange(result, path, token.index, token.index + markup.length, lineStarts, lines);

          const attributePattern = /([^\s=/>]+)\s*=\s*(["'])([\s\S]*?)\2/g;
          let attribute: RegExpExecArray | null;
          while ((attribute = attributePattern.exec(markup))) {
            const attributeName = this.xmlLocalName(attribute[1]);
            const valueOffset = attribute.index + attribute[0].indexOf(attribute[2]) + 1;
            this.addAbsoluteRange(
              result,
              `${path}/@${attributeName}`,
              token.index + valueOffset,
              token.index + valueOffset + attribute[3].length,
              lineStarts,
              lines
            );
          }

          if (!/\/\s*>$/.test(markup)) stack.push({ path, childCounts: {}, textCount: 0 });
        }
      }
      previousEnd = token.index + markup.length;
    }
    this.addXmlTextRange(raw, previousEnd, raw.length, stack, result, lineStarts, lines);
    return result;
  }

  private addXmlTextRange(
    raw: string,
    start: number,
    end: number,
    stack: XmlPathFrame[],
    target: Record<string, RawSourceRange[]>,
    lineStarts: number[],
    lines: string[]
  ): void {
    const frame = stack[stack.length - 1];
    if (!frame) return;
    const text = raw.slice(start, end);
    const leadingWhitespace = text.search(/\S/);
    if (leadingWhitespace < 0) return;
    const trailingWhitespace = text.length - text.trimEnd().length;
    frame.textCount++;
    this.addAbsoluteRange(
      target,
      `${frame.path}/#text[${frame.textCount}]`,
      start + leadingWhitespace,
      end - trailingWhitespace,
      lineStarts,
      lines
    );
  }

  private addAbsoluteRange(
    target: Record<string, RawSourceRange[]>,
    path: string,
    start: number,
    end: number,
    lineStarts: number[],
    lines: string[]
  ): void {
    for (let index = 0; index < lineStarts.length; index++) {
      const lineStart = lineStarts[index];
      const lineEnd = lineStart + lines[index].length;
      const rangeStart = Math.max(start, lineStart);
      const rangeEnd = Math.min(end, lineEnd);
      if (rangeEnd > rangeStart) {
        (target[path] ||= []).push({ line: index + 1, start: rangeStart - lineStart, end: rangeEnd - lineStart });
      }
      if (lineStart >= end) break;
    }
  }

  private xmlLocalName(name: string): string {
    const separator = name.indexOf(':');
    return separator < 0 ? name : name.slice(separator + 1);
  }
}
